'use client'

import Link from 'next/link'
import { useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'

import { signOut, updateWorkOSProfile } from '@/app/actions/auth'
import { updateUser } from '@/app/actions/user'
import { Text } from '@/blocks/Form/Text'
import { Textarea } from '@/blocks/Form/Textarea'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { User } from '@/payload-types'
import { createRichText } from '@/seed/helpers/lexical'
import { extractPlainText } from '@/utilities/extractPlainText'
import { formValidation } from '@/utilities/formValidation'
import { cn } from '@/utilities/ui'

import { ProfileSteps } from './ProfileStep'

import type { FieldValues, UseFormRegister } from 'react-hook-form'

type ProfileFormValues = {
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  email: string
  mobile?: string
  affiliation?: string
  bio?: string
}

export const ProfileForm = ({
  user,
  setOpenDrawer,
  setCurrentProfileStep,
  currentProfileStep,
  onUserUpdated,
}: {
  user: User
  setOpenDrawer: (open: boolean) => void
  setCurrentProfileStep: (step: ProfileSteps) => void
  currentProfileStep?: ProfileSteps
  onUserUpdated?: (updatedUser?: User) => Promise<void>
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailUpdated, setEmailUpdated] = useState(false)

  const methods = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email,
      username: user.profile?.username ?? '',
      mobile: user.profile?.mobile ?? '',
      affiliation: user.profile?.affiliation ?? '',
      //I need to convert the bio from the rich text to a string
      bio: extractPlainText(user.profile?.bio ?? undefined),
    },
  })

  const onSubmit = async (data: ProfileFormValues) => {
    setSubmitError(null)
    setIsSubmitting(true)

    const emailDiffers = (data.email ?? '').trim() !== (user.email ?? '').trim()
    const firstNameDiffers = (data.firstName ?? '').trim() !== (user.firstName ?? '').trim()
    const lastNameDiffers = (data.lastName ?? '').trim() !== (user.lastName ?? '').trim()

    try {
      const hasUserProfileFields =
        user.id &&
        (('firstName' in data && firstNameDiffers) ||
          ('lastName' in data && lastNameDiffers) ||
          emailDiffers)
      if (hasUserProfileFields && user) {
        const result = await updateWorkOSProfile({
          ...(firstNameDiffers && { firstName: data.firstName ?? '' }),
          ...(lastNameDiffers && { lastName: data.lastName ?? '' }),
          ...(emailDiffers && { email: (data.email ?? '').trim() }),
        })
        if (!result.success) {
          throw new Error(result.error ?? 'Failed to update User profile, please try again')
        }
      }
      const res = await updateUser({
        ...user,
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        profile: {
          username: data.username ?? '',
          mobile: data.mobile ?? '',
          affiliation: data.affiliation ?? '',
          bio: createRichText([data.bio ?? '']),
        },
      })

      if (res.status === 'error') {
        setSubmitError(res.message)
        return
      }

      const updatedUser = res.data

      // WorkOS sets emailVerified to false when email changes; show dialog so user can sign out and re-login.
      if (emailDiffers) {
        setEmailUpdated(true)
        return
      }

      if (updatedUser) {
        methods.reset({
          firstName: updatedUser.firstName ?? '',
          lastName: updatedUser.lastName ?? '',
          email: updatedUser.email ?? '',
          username: updatedUser.profile?.username ?? '',
          mobile: updatedUser.profile?.mobile ?? '',
          affiliation: updatedUser.profile?.affiliation ?? '',
          bio: extractPlainText(updatedUser.profile?.bio ?? undefined),
        })
      }
      setOpenDrawer(false)
      setCurrentProfileStep('socials')
      await onUserUpdated?.(updatedUser)
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred while submitting the form',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form id="profile-form" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="m-auto my-6 max-w-xl">
          <h2 className="font-family-body text-2xl font-semibold text-foreground">Profile</h2>
          <p className="mt-3 font-body text-card-foreground">
            Tell us a bit about yourself to help personalize your experience.
          </p>
          <div className="my-4 grid grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              {currentProfileStep === 'profile' ? (
                <Tooltip>
                  <TooltipTrigger className="w-full" asChild>
                    <span className="w-full">
                      <Text
                        name="email"
                        label="Email"
                        blockType="text"
                        placeholder="Enter email"
                        errors={methods.formState.errors}
                        validation={formValidation.email}
                        disabled={true}
                        register={methods.register as unknown as UseFormRegister<FieldValues>}
                        required
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="flex items-center text-xs">
                    <AlertCircle className="size-4 text-red-500" />
                    &nbsp;Email updates can be made once once you have completed the introduction.
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Text
                  name="email"
                  label="Email"
                  blockType="text"
                  placeholder="Enter email"
                  errors={methods.formState.errors}
                  validation={formValidation.email}
                  register={methods.register as unknown as UseFormRegister<FieldValues>}
                  required
                />
              )}
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="username"
                label="Username"
                blockType="text"
                placeholder="Enter Username"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="firstName"
                label="First Name"
                blockType="text"
                placeholder="Enter first name"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.firstName}
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="lastName"
                label="Last Name"
                blockType="text"
                placeholder="Enter last name"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.lastName}
              />
            </div>
            {/* TODO: Hide this on guided intro */}
            {user.introCompleted && (
              <div className="col-span-2 md:col-span-1">
                <Text
                  name="mobile"
                  label="Mobile"
                  blockType="text"
                  placeholder="Enter mobile number"
                  errors={methods.formState.errors}
                  register={methods.register as unknown as UseFormRegister<FieldValues>}
                  validation={formValidation.phone}
                />
              </div>
            )}
            <div
              className={cn('col-span-2', !user.introCompleted ? 'md:col-span-2' : 'md:col-span-1')}
            >
              <Text
                name="affiliation"
                label="Company Affiliation"
                blockType="text"
                placeholder="Enter company affiliation"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
              />
            </div>
            <div className="col-span-2">
              <Textarea
                name="bio"
                label="Bio"
                blockType="text"
                placeholder="Enter a bio"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
              />
            </div>
            {user.introCompleted && (
              <p className="col-span-2 text-[10px]">
                By providing your telephone number and email, you consent to receive recurring
                calls, texts, and emails from MAHA Action, including prerecorded messages and via
                automated methods, regarding our advocacy, campaigns, and events. Consent is not a
                condition of any donation or membership. Msg & data rates may apply. Msg frequency
                varies (typically 4-8 messages per month). We do not share your phone number or SMS
                opt-in data with third parties for their marketing purposes. For texts, reply STOP
                to opt out, HELP for help. For emails, click the &quot;unsubscribe&quot; link in the
                footer of any email if you no longer wish to receive them. View our{' '}
                <Link href="/terms-of-service" target="_blank">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="/privacy-policy" target="_blank">
                  Privacy Policy
                </Link>{' '}
                for more info.
              </p>
            )}
            <Button
              type="submit"
              className="col-span-2 block text-center"
              labelClassName="w-fit mx-auto"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
          <Dialog open={emailUpdated}>
            <DialogContent className="border-none bg-neutral-100" customCloseButton={true}>
              <DialogTitle>Email updated</DialogTitle>
              <DialogDescription>
                Your email has been updated. Please log in again to continue.
              </DialogDescription>
              <DialogClose asChild>
                <Button
                  onClick={async () => {
                    setEmailUpdated(false)
                    await signOut()
                  }}
                  variant="accent"
                >
                  Return to Login
                </Button>
              </DialogClose>
            </DialogContent>
          </Dialog>
          {submitError && (
            <p className="m-auto rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
              {submitError}
            </p>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
